import { useState } from "react";
import { Slider } from "@/components/ui/slider";
import { AnimatedNumber } from "./Counter";
import { Overline, RevealLines, FadeUp } from "./Reveal";

export default function RoiCalculator() {
  const [value, setValue] = useState(500);
  const [customers, setCustomers] = useState(3);
  const monthly = value * customers;

  return (
    <section className="dark-section bg-[#0A0A0A] text-white">
      <div className="container-x section-pad">
        <Overline light>The business case</Overline>
        <h2 className="font-display mt-6 max-w-4xl text-4xl font-semibold uppercase leading-[1.02] tracking-tight md:text-5xl lg:text-[4vw]">
          <RevealLines lines={["What if your website", "brought you just one", "extra customer?"]} />
        </h2>
        <FadeUp delay={0.15}>
          <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-white/60 md:text-lg">
            Move the sliders. A website isn't an expense — it's an asset that should pay for itself. No unrealistic promises, just simple math.
          </p>
        </FadeUp>

        <div className="mt-16 grid gap-10 lg:grid-cols-2 lg:gap-20">
          <FadeUp className="space-y-12">
            <div>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <span id="roi-value-label" className="text-xs uppercase tracking-normal text-white/70">Average customer value</span>
                <AnimatedNumber data-testid="roi-customer-value" value={value} className="font-display shrink-0 text-3xl font-bold tabular-nums" />
              </div>
              <Slider
                data-testid="roi-customer-value-slider"
                thumbProps={{ "aria-labelledby": "roi-value-label", "data-testid": "roi-customer-value-thumb" }}
                className="mt-3 min-h-11 [&_[role=slider]]:bg-white [&_[role=slider]]:border-white [&_.bg-primary]:bg-white"
                min={100}
                max={5000}
                step={50}
                value={[value]}
                onValueChange={(v) => setValue(v[0])}
              />
            </div>
            <div>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <span id="roi-customers-label" className="text-xs uppercase tracking-normal text-white/70">Additional customers / month</span>
                <AnimatedNumber data-testid="roi-customers-count" value={customers} prefix="" className="font-display shrink-0 text-3xl font-bold tabular-nums" />
              </div>
              <Slider
                data-testid="roi-customers-slider"
                thumbProps={{ "aria-labelledby": "roi-customers-label", "data-testid": "roi-customers-thumb" }}
                className="mt-3 min-h-11 [&_[role=slider]]:bg-white [&_[role=slider]]:border-white [&_.bg-primary]:bg-white"
                min={1}
                max={20}
                step={1}
                value={[customers]}
                onValueChange={(v) => setCustomers(v[0])}
              />
            </div>
          </FadeUp>

          <FadeUp delay={0.1} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
            <div className="min-w-0 rounded-2xl border border-white/10 bg-[#111] p-6 md:p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-white/50">Additional monthly revenue</p>
              <AnimatedNumber data-testid="roi-monthly-result" value={monthly} className="font-display mt-3 block min-h-[1.2em] text-3xl font-bold tabular-nums tracking-normal sm:text-4xl lg:text-5xl xl:text-6xl" />
            </div>
            <div className="min-w-0 rounded-2xl border border-white/10 bg-[#111] p-6 md:p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-white/50">Additional annual revenue</p>
              <AnimatedNumber data-testid="roi-annual-result" value={monthly * 12} className="font-display mt-3 block min-h-[1.2em] text-3xl font-bold tabular-nums tracking-normal sm:text-4xl lg:text-5xl xl:text-6xl" />
            </div>
          </FadeUp>
        </div>

        <FadeUp delay={0.2}>
          <p className="mt-10 text-xs text-white/40">Illustrative math only — results depend on your market, offer, and follow-up. We don't make revenue guarantees.</p>
        </FadeUp>
      </div>
    </section>
  );
}
